/**
 * The error correction level configuration
 */
const ErrorCorrectionLevel = {
  /** L error correction level*/
  L: { level: 'L', value: 0b01 },
  /** M error correction level*/
  M: { level: 'M', value: 0b00 },
  /** Q error correction level*/
  Q: { level: 'Q', value: 0b11 },
  /** H error correction level*/
  H: { level: 'H', value: 0b10 },
};

/**
 * The error correction level configuration
 * @class
 * @classdesc The error correction level configuration, possible values are 'L', 'M', 'Q', 'H'. Should throws error if the value is not one of the possible values, ensuring the underlying value is correct on creation
 */
export class QRErrorCorrectionLevel {
  /** @type {string} */
  #error_level;

  /** @type {number} */
  #value;

  /**
   * @constructor
   * @param {string} error_level - possible values are 'L', 'M', 'Q', 'H'
   */
  constructor(error_level) {
    const error_correction_level = ErrorCorrectionLevel[error_level];
    // console.log(error_correction_level);
    if (!error_correction_level) {
      throw new Error(
        "Error Correction Level must be one of 'L', 'M', 'Q', 'H'",
      );
    }

    this.#error_level = error_level;
    this.#value = error_correction_level.value;
  }

  /**
   * return the error correction level value
   * @returns {number}
   */
  get value() {
    return this.#value;
  }

  /**
   * return the error correction level
   * @returns {string}
   */
  get level() {
    return this.#error_level;
  }
}

/**
 * The mask pattern configuration
 * @type {Record<number, function(number, number): boolean>}
 */
const MaskPattern = {
  0: (x, y) => (x + y) % 2 == 0,
  1: (_x, y) => y % 2 == 0,
  2: (x, _y) => x % 3 == 0,
  3: (x, y) => (x + y) % 3 == 0,
  4: (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 == 0,
  5: (x, y) => ((x * y) % 2) + ((x * y) % 3) == 0,
  6: (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 == 0,
  7: (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 == 0,
};

/**
 * @class
 * @classdesc The Mask Pattern configuration, the mask pattern should be between 0 and 7, throws an error if the value is valid
 */
export class QRMaskPattern {
  /** @type {number} */
  #mask_pattern;

  /**
   * @constructor
   * @param {number} number
   */
  constructor(number) {
    if (number < 0 || number > 7) {
      throw new Error('Mask Pattern must be between 0 and 7!');
    }

    this.#mask_pattern = number;
  }

  /**
   * return the mask number
   * @returns {number}
   */
  get mask_number() {
    return this.#mask_pattern;
  }

  /**
   * return a function that calculate the mask from x y coordinate
   * @returns {function(number, number): number}
   */
  get mask_function() {
    return MaskPattern[this.#mask_pattern];
  }
}

/**
 * Options to be passed for the QR Code encoder
 * @class
 * @classdesc The options to be passed for the QR Code encoder, the options should contain the error correction level and the mask pattern
 */
export class QRCodeOptions {
  /** @type {QRErrorCorrectionLevel}*/
  #error_correction_level;

  /** @type {QRMaskPattern} */
  #mask_pattern;

  /**
   * @param {QRErrorCorrectionLevel} error_correction_level
   * @param {QRMaskPattern} mask_pattern
   */
  constructor(error_correction_level, mask_pattern) {
    this.#error_correction_level = error_correction_level;
    this.#mask_pattern = mask_pattern;
  }

  get bitstream() {
    // console.log(this.#error_correction_level.value);
    // console.log(this.#mask_pattern.mask_number);
    return (
      (this.#error_correction_level.value << 3) | this.#mask_pattern.mask_number
    );
  }

  /**
   * create a default QRCode Options
   * @returns {QRCodeOptions}
   */
  static default() {
    return new QRCodeOptions(
      new QRErrorCorrectionLevel('L'),
      new QRMaskPattern(0),
    );
  }

  /**
   * Create a QRCodeOptions from the error correction level and mask pattern, this might throw an error if the error correction level or mask pattern is invalid
   * @param {string} errorCorrectionLevel - The error correction level valid values are 'L', 'M', 'Q', 'H'
   * @param {number} maskPattern - The mask pattern value
   * @return {QRCodeOptions}
   * @throws {Error} - If the error correction level or mask pattern is invalid
   */
  static from(errorCorrectionLevel, maskPattern) {
    const errLevel = new QRErrorCorrectionLevel(errorCorrectionLevel);
    const mask = new QRMaskPattern(maskPattern);
    return new QRCodeOptions(errLevel, mask);
  }

  /**
   * Get the metadata of the QRCodeOptions
   * @returns {object} The metadata of the QRCodeOptions
   */
  get metadata() {
    return {
      error_correction_level: this.#error_correction_level,
      mask_pattern: this.#mask_pattern,
    };
  }

  get errorCorrectionLevel() {
    return this.#error_correction_level;
  }

  get maskNumber() {
    return this.#mask_pattern.mask_number;
  }
}
